import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard,
  TrendingUp,
  Users,
  AlertCircle,
  Edit,
  Ban,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  DollarSign,
  FileText,
  History,
  XCircle,
  Clock,
  Shield,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

const CreditManagement = () => {
  const [activeTab, setActiveTab] = useState('AGENTS'); // AGENTS | CYCLES | TRANSACTIONS
  const [overview, setOverview] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [agentTxns, setAgentTxns] = useState({}); // agent_id -> [txns]

  // Record Payment modal
  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    method: 'CASH',
    reference: '',
    notes: '',
    payment_date: new Date().toISOString().slice(0, 10),
  });
  const [paymentSaving, setPaymentSaving] = useState(false);

  // Billing cycles tab
  const [cycles, setCycles] = useState([]);
  const [cycleFilter, setCycleFilter] = useState('');
  const [cyclePage, setCyclePage] = useState(1);
  const [cyclePages, setCyclePages] = useState(1);

  // Transactions tab
  const [allTxns, setAllTxns] = useState([]);
  const [txnTypeFilter, setTxnTypeFilter] = useState('');
  const [txnPage, setTxnPage] = useState(1);
  const [txnPages, setTxnPages] = useState(1);

  // ---------- data fetchers ----------
  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, agentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/credit/overview`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/credit/agents${filter !== 'ALL' ? `?status=${filter}` : ''}`, { withCredentials: true }),
      ]);
      setOverview(overviewRes.data);
      setAgents(agentsRes.data.agents || []);
    } catch (error) {
      console.error('Error fetching credit data:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchCycles = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(cyclePage), limit: '20' });
      if (cycleFilter) params.set('status', cycleFilter);
      const r = await axios.get(`${API_URL}/api/admin/credit/billing-cycles?${params}`, { withCredentials: true });
      setCycles(r.data.cycles || []);
      setCyclePages(r.data.total_pages || 1);
    } catch (e) {
      console.error('Error fetching cycles', e);
    }
  }, [cycleFilter, cyclePage]);

  const fetchTxns = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(txnPage), limit: '50' });
      if (txnTypeFilter) params.set('transaction_type', txnTypeFilter);
      const r = await axios.get(`${API_URL}/api/admin/credit/transactions?${params}`, { withCredentials: true });
      setAllTxns(r.data.transactions || []);
      setTxnPages(r.data.total_pages || 1);
    } catch (e) {
      console.error('Error fetching txns', e);
    }
  }, [txnTypeFilter, txnPage]);

  useEffect(() => {
    if (activeTab === 'AGENTS') fetchAgents();
    else if (activeTab === 'CYCLES') fetchCycles();
    else if (activeTab === 'TRANSACTIONS') fetchTxns();
  }, [activeTab, fetchAgents, fetchCycles, fetchTxns]);

  // ---------- agent actions ----------
  const handleSuspend = async (agentId) => {
    if (!window.confirm("Suspend this agent's credit?")) return;
    try {
      await axios.post(`${API_URL}/api/admin/credit/suspend/${agentId}`, null, { withCredentials: true });
      fetchAgents();
    } catch (error) {
      alert('Failed to suspend credit: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleActivate = async (agentId) => {
    try {
      await axios.post(`${API_URL}/api/admin/credit/activate/${agentId}`, null, { withCredentials: true });
      fetchAgents();
    } catch (error) {
      alert('Failed to activate credit: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleGrantGrace = async (agent) => {
    const reason = prompt('Reason for allowing bookings while overdue:', 'Trusted agent — payment expected');
    if (reason === null) return;
    const until = prompt('Grace until (YYYY-MM-DD) — leave blank for open-ended:', '');
    try {
      await axios.post(
        `${API_URL}/api/admin/credit/agent/${agent.id}/grant-grace`,
        { reason, until: until || null },
        { withCredentials: true },
      );
      fetchAgents();
    } catch (error) {
      alert('Failed to grant grace: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRevokeGrace = async (agentId) => {
    if (!window.confirm('Revoke overdue booking grace for this agent?')) return;
    try {
      await axios.post(`${API_URL}/api/admin/credit/agent/${agentId}/revoke-grace`, null, { withCredentials: true });
      fetchAgents();
    } catch (error) {
      alert('Failed to revoke grace: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateLimit = async (agentId) => {
    const newLimit = prompt('Enter new credit limit (₹):');
    if (!newLimit || isNaN(newLimit)) return;
    try {
      const params = new URLSearchParams({ credit_limit: newLimit });
      await axios.put(`${API_URL}/api/admin/credit/update/${agentId}?${params}`, null, { withCredentials: true });
      fetchAgents();
    } catch (error) {
      alert('Failed to update limit: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCloseCycle = async (agentId) => {
    if (!window.confirm('Close the current credit cycle for this agent? This will create an invoice.')) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/credit/close-cycle/${agentId}`, null, { withCredentials: true });
      alert(`Cycle closed — invoice ${r.data.invoice_number}. Outstanding: ${inr(r.data.outstanding)}`);
      fetchAgents();
    } catch (error) {
      alert('Failed to close cycle: ' + (error.response?.data?.detail || error.message));
    }
  };

  const openRecordPayment = (agent) => {
    setPaymentModal(agent);
    setPaymentForm({
      amount: String(agent.credit_used || 0),
      method: 'CASH',
      reference: '',
      notes: '',
      payment_date: new Date().toISOString().slice(0, 10),
    });
  };

  const submitRecordPayment = async () => {
    if (!paymentModal) return;
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      alert('Enter a valid payment amount.');
      return;
    }
    setPaymentSaving(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/credit/agent/${paymentModal.id}/record-payment`,
        {
          amount,
          method: paymentForm.method,
          reference: paymentForm.reference || null,
          notes: paymentForm.notes || null,
          payment_date: paymentForm.payment_date || null,
        },
        { withCredentials: true },
      );
      alert(`Payment recorded (${r.data.transaction_id}). Credit used: ${inr(r.data.credit_used_after)}`);
      setPaymentModal(null);
      fetchAgents();
    } catch (error) {
      alert('Payment failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPaymentSaving(false);
    }
  };

  const expandAgent = async (agent) => {
    const next = expandedAgent === agent.id ? null : agent.id;
    setExpandedAgent(next);
    if (next && !agentTxns[agent.id]) {
      try {
        const r = await axios.get(
          `${API_URL}/api/admin/credit/transactions?agent_id=${agent.id}&limit=20`,
          { withCredentials: true },
        );
        setAgentTxns((prev) => ({ ...prev, [agent.id]: r.data.transactions || [] }));
      } catch (e) {
        console.error('txn fetch error', e);
      }
    }
  };

  // ---------- billing cycle actions ----------
  const handleMarkCyclePaid = async (cycle) => {
    const amt = prompt(`Mark payment received for invoice ${cycle.invoice_number}.\nOutstanding: ${inr(cycle.outstanding)}\nEnter amount received (₹):`, String(cycle.outstanding));
    if (!amt || isNaN(amt)) return;
    try {
      const params = new URLSearchParams({ amount_paid: amt });
      await axios.post(`${API_URL}/api/admin/credit/billing/${cycle.id}/mark-paid?${params}`, null, { withCredentials: true });
      fetchCycles();
    } catch (e) {
      alert('Failed to mark paid: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (loading && activeTab === 'AGENTS' && !overview) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading credit data...</p>
        </div>
      </div>
    );
  }

  // ============ RENDER ============
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Credit Management</h1>
          <p className="text-gray-600">Manage agent credit limits, payments, and billing cycles</p>
        </div>
        <button
          onClick={async () => {
            if (!window.confirm('Run the auto-close cycles job now? This is normally scheduled daily at 02:00 IST.')) return;
            try {
              const r = await axios.post(`${API_URL}/api/admin/credit/cron/close-cycles-now`, null, { withCredentials: true });
              const s = r.data?.close_summary;
              alert(`Cron complete — closed ${s?.closed || 0} cycles, skipped ${s?.skipped || 0}. Overdue recomputed for ${r.data?.overdue_summary?.agents_updated || 0} agents.`);
              if (activeTab === 'AGENTS') fetchAgents();
              if (activeTab === 'CYCLES') fetchCycles();
            } catch (e) {
              alert('Failed to run cron: ' + (e.response?.data?.detail || e.message));
            }
          }}
          className="px-4 py-2 bg-[#1B375B] text-white rounded-lg hover:bg-[#0d2037] font-semibold text-sm flex items-center gap-2"
          data-testid="run-cron-btn"
        >
          <Clock className="w-4 h-4" /> Run Cycle Close Now
        </button>
      </div>

      {/* Overview */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-gray-600 text-sm">Total Credit Agents</p>
            <p className="text-2xl font-bold text-gray-800">{overview.total_credit_agents}</p>
            <p className="text-xs text-gray-500 mt-2">
              {overview.active_agents} Active · {overview.suspended_agents} Suspended
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3">
              <CreditCard className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-gray-600 text-sm">Total Credit Issued</p>
            <p className="text-2xl font-bold text-gray-800">{inr(overview.total_credit_issued)}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-gray-600 text-sm">Credit Utilized</p>
            <p className="text-2xl font-bold text-gray-800">{inr(overview.total_credit_used)}</p>
            <p className="text-xs text-gray-500 mt-2">{overview.utilization_percentage}% utilization</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-3">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <p className="text-gray-600 text-sm">Overdue Amount</p>
            <p className="text-2xl font-bold text-red-600">{inr(overview.total_overdue_amount)}</p>
            <p className="text-xs text-gray-500 mt-2">{overview.agents_with_overdue} agents with overdue</p>
          </div>
        </div>
      )}

      {/* Main Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-6">
        <div className="flex gap-2 p-2 border-b">
          {[
            { k: 'AGENTS', label: 'Credit Agents', icon: Users },
            { k: 'CYCLES', label: 'Billing Cycles', icon: FileText },
            { k: 'TRANSACTIONS', label: 'Transactions', icon: History },
          ].map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              data-testid={`credit-tab-${k.toLowerCase()}`}
              onClick={() => setActiveTab(k)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
                activeTab === k ? 'bg-[#1B375B] text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* ------ AGENTS TAB ------ */}
        {activeTab === 'AGENTS' && (
          <>
            <div className="flex gap-2 p-4 border-b">
              {['ALL', 'ACTIVE', 'SUSPENDED'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    filter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f === 'ALL' ? `All (${overview?.total_credit_agents || 0})` : f === 'ACTIVE' ? `Active (${overview?.active_agents || 0})` : `Suspended (${overview?.suspended_agents || 0})`}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Agent</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Credit Limit</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Used</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Available</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Pay Day</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {agents.map((agent) => (
                    <React.Fragment key={agent.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-800">{agent.agency_name}</p>
                          <p className="text-xs text-gray-500">{agent.full_name}</p>
                        </td>
                        <td className="px-4 py-3 font-semibold">{inr(agent.credit_limit)}</td>
                        <td className="px-4 py-3 text-red-600 font-semibold">{inr(agent.credit_used)}</td>
                        <td className="px-4 py-3 text-green-600 font-semibold">{inr(agent.credit_available)}</td>
                        <td className="px-4 py-3">
                          {agent.credit_status === 'ACTIVE' ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Active</span>
                          ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Suspended</span>
                          )}
                          {agent.overdue_amount > 0 && (
                            <p className="text-xs text-red-600 mt-1">Overdue: {inr(agent.overdue_amount)}</p>
                          )}
                          {agent.credit_overdue_grace_enabled && (
                            <p className="text-xs text-amber-700 mt-1 font-semibold">Grace active</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {agent.credit_terms_days || 7}d terms
                          {agent.payment_day ? ` · ${agent.payment_day}` : ''}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openRecordPayment(agent)}
                              className="p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Record Payment"
                              data-testid={`record-payment-${agent.id}`}
                              disabled={(agent.credit_used || 0) === 0}
                            >
                              <DollarSign className={`w-4 h-4 ${(agent.credit_used || 0) === 0 ? 'text-gray-300' : 'text-emerald-600'}`} />
                            </button>
                            <button
                              onClick={() => handleCloseCycle(agent.id)}
                              className="p-2 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Close Current Cycle"
                              data-testid={`close-cycle-${agent.id}`}
                              disabled={(agent.credit_used || 0) === 0}
                            >
                              <FileText className={`w-4 h-4 ${(agent.credit_used || 0) === 0 ? 'text-gray-300' : 'text-purple-600'}`} />
                            </button>
                            <button onClick={() => handleUpdateLimit(agent.id)} className="p-2 hover:bg-blue-50 rounded-lg" title="Edit Limit">
                              <Edit className="w-4 h-4 text-blue-600" />
                            </button>
                            {(agent.overdue_amount || 0) > 0 && !agent.credit_overdue_grace_enabled && (
                              <button onClick={() => handleGrantGrace(agent)} className="p-2 hover:bg-amber-50 rounded-lg" title="Allow booking despite overdue">
                                <Shield className="w-4 h-4 text-amber-600" />
                              </button>
                            )}
                            {agent.credit_overdue_grace_enabled && (
                              <button onClick={() => handleRevokeGrace(agent.id)} className="p-2 hover:bg-gray-100 rounded-lg" title="Revoke grace">
                                <Shield className="w-4 h-4 text-gray-500" />
                              </button>
                            )}
                            {agent.credit_status === 'ACTIVE' ? (
                              <button onClick={() => handleSuspend(agent.id)} className="p-2 hover:bg-red-50 rounded-lg" title="Suspend">
                                <Ban className="w-4 h-4 text-red-600" />
                              </button>
                            ) : (
                              <button onClick={() => handleActivate(agent.id)} className="p-2 hover:bg-green-50 rounded-lg" title="Activate">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              </button>
                            )}
                            <button onClick={() => expandAgent(agent)} className="p-2 hover:bg-gray-100 rounded-lg" title="History">
                              {expandedAgent === agent.id ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedAgent === agent.id && (
                        <tr>
                          <td colSpan="7" className="px-4 py-4 bg-gray-50">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Email</p>
                                <p className="text-sm font-medium">{agent.email}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Phone</p>
                                <p className="text-sm font-medium">{agent.phone}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Next Due Date</p>
                                <p className="text-sm font-medium">{agent.next_due_date || 'N/A'}</p>
                              </div>
                            </div>
                            <p className="text-sm font-semibold text-gray-700 mb-2">Recent Credit Activity</p>
                            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                              {(agentTxns[agent.id] || []).length > 0 ? (
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100 text-xs text-gray-600">
                                    <tr>
                                      <th className="px-3 py-2 text-left">Date</th>
                                      <th className="px-3 py-2 text-left">Type</th>
                                      <th className="px-3 py-2 text-left">Description</th>
                                      <th className="px-3 py-2 text-right">Amount</th>
                                      <th className="px-3 py-2 text-right">Balance After</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(agentTxns[agent.id] || []).map((t) => {
                                      const isPayment = t.transaction_type === 'PAYMENT';
                                      return (
                                        <tr key={t.id} className="border-t">
                                          <td className="px-3 py-2 text-gray-600">{new Date(t.created_at).toLocaleString()}</td>
                                          <td className="px-3 py-2">
                                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                              isPayment ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                                            }`}>
                                              {isPayment ? 'Payment' : 'Utilization'}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-gray-700">{t.description || (isPayment ? t.method : t.booking_id)}</td>
                                          <td className={`px-3 py-2 text-right font-semibold ${isPayment ? 'text-green-700' : 'text-red-700'}`}>
                                            {isPayment ? '-' : '+'} {inr(t.amount)}
                                          </td>
                                          <td className="px-3 py-2 text-right text-gray-700">{inr(t.credit_after)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              ) : (
                                <p className="text-sm text-gray-500 p-4 text-center">No credit activity yet</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {agents.length === 0 && (
              <div className="text-center py-12"><p className="text-gray-500">No credit agents found</p></div>
            )}
          </>
        )}

        {/* ------ BILLING CYCLES TAB ------ */}
        {activeTab === 'CYCLES' && (
          <>
            <div className="flex gap-2 p-4 border-b">
              {['', 'PENDING', 'OVERDUE', 'PARTIAL_PAID', 'PAID'].map((f) => (
                <button
                  key={f || 'ALL'}
                  onClick={() => { setCycleFilter(f); setCyclePage(1); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                    cycleFilter === f ? 'bg-[#1B375B] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f || 'All'}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left">Invoice #</th>
                    <th className="px-4 py-3 text-left">Agency</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-right">Paid</th>
                    <th className="px-4 py-3 text-right">Outstanding</th>
                    <th className="px-4 py-3 text-left">Due Date</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {cycles.length > 0 ? cycles.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">{c.invoice_number || c.id}</td>
                      <td className="px-4 py-3 font-medium">{c.agency_name || '-'}</td>
                      <td className="px-4 py-3 text-right">{inr(c.total_amount)}</td>
                      <td className="px-4 py-3 text-right text-green-700">{inr(c.amount_paid)}</td>
                      <td className="px-4 py-3 text-right text-red-700 font-semibold">{inr(c.outstanding)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {c.due_date ? new Date(c.due_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          c.status === 'PAID' ? 'bg-green-100 text-green-800' :
                          c.status === 'OVERDUE' ? 'bg-red-100 text-red-800' :
                          c.status === 'PARTIAL_PAID' ? 'bg-amber-100 text-amber-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <a
                            href={`${API_URL}/api/admin/credit/billing/${c.id}/invoice.pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2.5 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200 font-semibold inline-flex items-center gap-1"
                            data-testid={`invoice-dl-${c.id}`}
                          >
                            Invoice
                          </a>
                          {['PENDING', 'OVERDUE', 'PARTIAL_PAID'].includes(c.status) && (
                            <button
                              onClick={() => handleMarkCyclePaid(c)}
                              className="px-3 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700 font-semibold"
                              data-testid={`mark-paid-${c.id}`}
                            >
                              Mark Paid
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="8" className="text-center text-gray-500 py-8">No billing cycles yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {cyclePages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-gray-600">Page {cyclePage} of {cyclePages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setCyclePage((p) => Math.max(1, p - 1))} disabled={cyclePage === 1} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                  <button onClick={() => setCyclePage((p) => Math.min(cyclePages, p + 1))} disabled={cyclePage === cyclePages} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ------ TRANSACTIONS TAB ------ */}
        {activeTab === 'TRANSACTIONS' && (
          <>
            <div className="flex gap-2 p-4 border-b">
              {['', 'PAYMENT', 'BOOKING', 'TICKET_DEBIT', 'UTILIZATION'].map((f) => (
                <button
                  key={f || 'ALL'}
                  onClick={() => { setTxnTypeFilter(f); setTxnPage(1); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                    txnTypeFilter === f ? 'bg-[#1B375B] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f || 'All'}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Agent</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Method / Ref</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3 text-right">Balance After</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {allTxns.length > 0 ? allTxns.map((t) => {
                    const isPayment = t.transaction_type === 'PAYMENT';
                    return (
                      <tr key={t.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-600">{new Date(t.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{t.agency_name || '-'}</p>
                          <p className="text-xs text-gray-500">{t.agent_full_name}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            isPayment ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {isPayment ? 'Payment' : 'Utilization'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {isPayment ? (
                            <span>
                              {t.method || 'CASH'}{t.reference ? ` · ${t.reference}` : ''}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500 font-mono">{t.booking_id || t.description}</span>
                          )}
                        </td>
                        <td className={`px-4 py-3 text-right font-semibold ${isPayment ? 'text-green-700' : 'text-red-700'}`}>
                          {isPayment ? '-' : '+'} {inr(t.amount)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">{inr(t.credit_after)}</td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="6" className="text-center text-gray-500 py-8">No transactions</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {txnPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-gray-600">Page {txnPage} of {txnPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setTxnPage((p) => Math.max(1, p - 1))} disabled={txnPage === 1} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                  <button onClick={() => setTxnPage((p) => Math.min(txnPages, p + 1))} disabled={txnPage === txnPages} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Record Payment Modal */}
      {paymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="record-payment-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Record Payment</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {paymentModal.agency_name} · Outstanding {inr(paymentModal.credit_used)}
                </p>
              </div>
              <button onClick={() => setPaymentModal(null)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Received (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  data-testid="payment-method-select"
                >
                  {['CASH', 'BANK_TRANSFER', 'CHEQUE', 'UPI', 'DD', 'OTHER'].map((m) => (
                    <option key={m} value={m}>{m.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference No. (optional)</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  placeholder="Cheque no. / UTR / UPI txn ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                <input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  rows="2"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button onClick={() => setPaymentModal(null)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">
                Cancel
              </button>
              <button
                onClick={submitRecordPayment}
                disabled={paymentSaving}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
                data-testid="record-payment-submit"
              >
                {paymentSaving ? 'Saving...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditManagement;
